/**
 * Cartera de clientes — Sprint 10 redesign.
 *
 * Página de entrada del auditor cada día. Search prominente, action
 * principal "Nuevo cliente", grid de cards con info clave + chevron.
 * Empty state amistoso. Loading skeleton.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { type ClientSummary, clientsApi } from '../api/clients.js';
import {
  ButtonLink,
  EmptyState,
  LoadingSkeleton,
  PageHeader,
  SearchInput,
  Badge,
} from '../components/ui.js';

export function ClientsListPage(): JSX.Element {
  const [clients, setClients] = useState<ClientSummary[] | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setClients(null);
    setError(null);
    const handle = setTimeout(() => {
      clientsApi
        .list(search.trim() || undefined)
        .then((d) => {
          if (!cancelled) setClients(d.clients);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : 'Error cargando clientes');
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search]);

  return (
    <main className="mx-auto max-w-6xl px-6 py-8 space-y-6">
      <PageHeader
        eyebrow="CLIENTES"
        title="Cartera de clientes"
        subtitle="Búsqueda por nombre o RNC. Click en una tarjeta para ver sitios y levantamientos."
        actions={
          <ButtonLink to="/clients/new" size="md">
            + Nuevo cliente
          </ButtonLink>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nombre o RNC…" />
        {clients !== null && (
          <p className="text-xs text-slate-500 sm:whitespace-nowrap">
            {clients.length} {clients.length === 1 ? 'cliente' : 'clientes'}
            {search && ` para "${search}"`}
          </p>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700"
        >
          {error}
        </div>
      )}

      {clients === null && !error && <LoadingSkeleton rows={4} />}

      {clients?.length === 0 && (
        <EmptyState
          icon={
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="h-6 w-6"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          }
          title={search ? 'Sin resultados' : 'Aún no hay clientes'}
          description={
            search
              ? `Ningún cliente coincide con "${search}". Prueba con menos palabras o RNC parcial.`
              : 'Empieza creando tu primer cliente para registrar sus sitios y arrancar levantamientos.'
          }
          {...(!search ? { cta: { label: '+ Crear primer cliente', to: '/clients/new' } } : {})}
        />
      )}

      {clients && clients.length > 0 && (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {clients.map((c) => (
            <li key={c.id}>
              <Link
                to={`/clients/${c.id}`}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 hover:border-brand-500 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-base font-bold text-brand-700">
                      {initials(c.name)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-brand-900 truncate">{c.name}</p>
                      <p className="mt-0.5 text-xs text-slate-500 truncate">
                        {c.sector ?? 'Sin sector'}
                        {c.rnc && ` · RNC ${c.rnc}`}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {c.sector && <Badge tone="slate">{c.sector}</Badge>}
                        <Badge tone="brand">Activo</Badge>
                      </div>
                    </div>
                  </div>
                  <span
                    className="text-slate-300 group-hover:text-brand-500 transition-colors shrink-0"
                    aria-hidden
                  >
                    <svg
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="h-5 w-5"
                    >
                      <polyline
                        points="7 4 13 10 7 16"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
